/**
 * Static field descriptions for migration record fields.
 * Nested by entity type (client | caregiver | employee), then by field_name.
 * Add or update descriptions here as needed.
 */

export const FIELD_DESCRIPTIONS: Record<string, Record<string, string>> = {
  client: {
    "Personal Information": `Personal Information with dob and SSN.
  You need to verify all details in the Personal Information section. 
  You need to update the address for all clients in Carebravo as per the details available in Caryfy.
  You need to update SSN and DOB for any random clients in Carebravo if they are missing.
  If the DOB is not available in Caryfy, you can enter 01/01/1900 as the DOB and save the data.
  If the SSN is not available in Caryfy but you still need to update the address and other data, you can enter 0000000 in Carebravo and save the data.
  If you find that the same field data is missing for all clients, then you do not need to add the data. Instead, raise an issue and record it in the sheet.`,

    "Payment Terms": `if exist in 1.0 so must be visible here.
You need to verify the Call Type name, Shift Type, and Bill Rate value according to Caryfy 1.0.
If a Call Type is missing in Carebravo but is available in Caryfy, you can add it in Carebravo as per Caryfy and also mention it in the sheet.
If the Created By name is missing, you should mention it as an issue in the sheet.
If a call type name is not available in Caryfy, you do not need to update the data. Simply mention it in the sheet.
If you find any extra Call Types in Carebravo compared to Caryfy, do not update the data. Simply mention it in the sheet.`,

    "Payer": `Medicaid Payer Group: Medicate, , Authorization date

Verify all the details as per Caryfy.

In Carebravo, you will see all the program names selected. You need to go to Caryfy, open the About Me section, and check which programs are selected there. Then, select the same programs in Carebravo and update the data in the Payer Group section accordingly.

Program name : in Carebravo all program is showing selected so u need to check in Caryfyf in 

You need to add Monthly and Weekly Hours data in Carebravo, but there are two conditions:

1. If data is available in only one section (Weekly or Monthly):
Select the respective Weekly or Monthly radio button.
Select the correct Call Type.
Add the data in the particular Call Type section.

2. If data is available in both sections in Caryfy:
Add the data only in the Weekly section in Carebravo.
If only one Call Type is available, you can directly select it and add the data in the respective section.

If multiple Call Types are showing under the payer:
Go to the Schedule Shifts section.
Check which Call Type has shifts created.
Select only that Call Type.
Add the data in that particular section.

In Caryfy, if the Private Pay payer is selected, the payer screen will not be visible. This is not an issue.
You can simply mention in the sheet that the payer selected is Private Pay, so Medicaid data cannot be updated for that client.

3. Skill Nursing section :
In Carebravo, you will see the Skilled Nursing – Monthly section. You do not need to add any additional data there. You only need to fill in the Authorization Number, Start Date, and End Date, using the same details provided in the upper section.`,

    "Diagnosis": `In the Diagnosis section, you need to verify the Diagnosis Name, Code, and Source as per Caryfy.
If a Diagnosis is missing in Carebravo but is available in Caryfy, you need to add it in Carebravo.
If the Diagnosis code is not available in Caryfy, a dash (-) will appear in Carebravo. In that case:
Mention the Diagnosis name along with the Client name in the sheet.
Delete that Diagnosis from Carebravo.
In Carebravo, you must verify the Diagnosis Type. It should be the same as in Caryfy.
If it is different, delete the existing Diagnosis in Carebravo.
Then add the same Diagnosis again as per Caryfy.
If you find any extra Diagnosis in Carebravo that is not available in Caryfy, do not make any changes. Just mention it in the sheet.`,

    "Plan Of Care : Care Tasks": `In the Care Task section, you need to verify that the same tasks selected in Caryfy are also selected in Carebravo. If any data is missing, you need to add it in Carebravo.
If the data in the Goal section is missing, you only need to mention it in the sheet because you cannot add that data manually.
In the Care Task section, open one task and verify the total count of all tasks.
If you find any other additional task selected, do not update it. Simply mention it in the sheet.`,

    "Plan Of Care : Care Details": `In the Care Details section, you need to verify that the same tasks selected in Caryfy are also selected in Carebravo. If any data is missing, you need to add it in Carebravo.
If you find that the Goal section data is missing, you only need to mention it in the sheet because you are not allowed to add that data.
In the Care Details section:
Open one task.
Verify the counts for all tasks.
If any other additional task is selected, do not update it. Just mention it in the sheet.
If there is no data available in Caryfy, then nothing will be shown in Carebravo.`,

    "Assignment": `On the Assignment screen, the data selected in Caryfy should be the same as in Carebravo.
If it is not selected in Carebravo, you need to select it and save the data.

In the Scheduler section in Caryfy, an Admin user may also be selected.
You need to identify the role, and the name should appear in the respective role field accordingly.
If you find such data, you only need to mention it in the sheet.

If you find any other additional data selected, do not update it. Just mention it in the sheet.`,

    "Important Dates": `In the Important Dates section, you need to verify that the data matches what is available in Caryfy.
If a date is not selected in Carebravo according to Caryfy, you need to select the correct date and save it.
If you see 01/01/1900 selected as the Start of Case date in Carebravo, but the Start of Case field is not selected in Caryfy, you need to mention this as an issue in the sheet along with the client’s UUID.
Sometimes, a Discharge Form may appear in Caryfy. You can ignore it`,

    "Referral Information": `In the Referral Information section, you need to verify that the data matches what is available in Caryfy.
If any data is missing in Carebravo according to Caryfy, you need to add it.
If different data is selected in Carebravo, do not update it. Just mention it in the sheet.
In Caryfy, the Default Referred By radio button may be selected by default. You can ignore this.`,

    "Client Document": `In the Client Documents section, you need to match the latest (top) documents.
You must verify the Created Date of the document. It should be the same in Caryfy and Carebravo.
In Caryfy, there is no option for an Expiry Date, so this is not an issue. You can ignore it.
If you find any random documents missing, download them from Caryfy and upload them to Carebravo.
If multiple documents are missing for all clients, mention this in the sheet.`,

    "Other Contacts": `In the Other Contact section, you need to verify that the data in Carebravo matches the data in Caryfy.
If any data is missing in Carebravo, you need to add it as per Caryfy.
In Caryfy, you must click Edit to view the full information of the Other Contact and verify all the details.
Since the address is available for all Other Contacts in Caryfy, you need to update the address in Carebravo accordingly.
If any extra Other Contact is showing in Carebravo, do not update it.`,

    "Client Medication": `In the Client Medication section, you need to verify that the number of medications in Carebravo matches the medications available in Caryfy.
You must also verify all the columns such as Dosage and other details. The information should be the same as in Caryfy.
If any medication is missing, you need to add it in Carebravo.
If any data does not match, you must update it in Carebravo according to Caryfy.`,

    "Check Health Information": `On the Health Information screen, you need to verify that the data selected in Caryfy is the same in Carebravo.
If any data is missing in Carebravo, you need to select it and save it as per Caryfy.
If the mobile number is not shown in the list (for example, in the Referral Source section), you can ignore it. It is not an issue.
If different data is selected in Carebravo, do not update it. Just mention it in the sheet.`,
  },

  caregiver: {
    // Add caregiver-specific field descriptions here
    // "Personal Information": `...`,
    "Personal Information": `Personal Information with dob and SSN.
You need to verify all details in the Personal Information section.
You need to update the address for all caregivers in Carebravo as per the data in Caryfy.
You need to update SSN and DOB for any random caregivers in Carebravo if they are missing.
If the DOB is not available in Caryfy, you can enter 01/01/1900 as the DOB and save the data.
If the SSN is not available in Caryfy but you need to update the address and other data, you can enter 0000000 in Carebravo and save the data.
If you find that the same field data is missing for all caregivers, then do not add the data. Instead, raise an issue and record it in the sheet.`,

    "Payroll Term": `Caregiver Payroll Terms, if exist in 1.0 so must be visible here. 
You need to verify the Call Type name, Shift Type, and Bill Rate value according to Caryfy 1.0. 
If a Call Type is missing in Carebravo but is available in Caryfy, you can add it in Carebravo as per Caryfy and also mention it in the sheet. 
If the Created By name is missing, you should mention it as an issue in the sheet. 
If a call type name is not available in Caryfy, you do not need to update the data. Simply mention it in the sheet. 
If you find any extra Call Types in Carebravo compared to Caryfy, do not update the data. Simply mention it in the sheet.`,

    "Unavailability": `if exist in 1.0 so must be visible here.
In the Caregiver Unavailability section, the same data has already been added in Caryfy. You need to verify it in Carebravo.
If any data is missing, you should add it in Carebravo exactly as it appears in Caryfy.
If any data is incorrect (such as date, time, etc.), you need to report it in the sheet.
If any extra record is showing, you should not update or delete anything. Leave it as it is and just mention it in the sheet.`,

    "Documents": `In the Client Documents section, you need to match the latest (top) documents.
You must verify the Created Date of the document. It should be the same in Caryfy and Carebravo.
In Caryfy, there is no option for an Expiry Date, so this is not an issue. You can ignore it.
In Carebravo, documents uploaded in the Skill Section will be shown there.
You need to verify these documents as per Caryfy.
In the Skill Section, documents will have an Expiry Date, so you must verify the expiry date.
If you find any random documents missing, download them from Caryfy and upload them to Carebravo.
If multiple documents are missing for all Caregivers, mention this in the sheet.`,

    "Skills": `In the Skill section, the data selected in Carebravo should exactly match the data available in Caryfy.
If you find any missing data, you need to update it in Carebravo according to the data in Caryfy.
In the Skill section, any documents uploaded in Caryfy will be visible in the Caregiver Document section. You need to verify them there. If any documents are missing, you must download them from Caryfy and upload them to Carebravo.
In the Skill section, you will see an Internal Document section in Caryfy, but this section is not available in Carebravo. Therefore, if there are any internal documents in Caryfy, you need to go to the Caregiver Document section in Carebravo and upload them there.`
  },

  employee: {
    // Add employee-specific field descriptions here
    // "Personal Information": `...`,
    "Personal Information": `personal info with designation must be RN, LPN, Health coach if role is nurse.
You need to verify all details in the Personal Information section.
You need to update the address for all EMployee and assessor in Carebravo as per the data in Caryfy.
You need to update SSN and DOB for any random EMployee and assessor in Carebravo if they are missing.
If the DOB is not available in Caryfy, you can enter 01/01/1900 as the DOB and save the data.
If the SSN is not available in Caryfy but you need to update the address and other data, you can enter 0000000 in Carebravo and save the data.
If you find that the same field data is missing for all Employee and Assessor , then do not add the data. Instead, raise an issue and record it in the sheet.`,

    "Payroll Term": `if exist in 1.0 so must be visible here.
You need to verify the Call Type name, Shift Type, and Bill Rate value according to Caryfy 1.0.
If a Call Type is missing in Carebravo but is available in Caryfy, you can add it in Carebravo as per Caryfy and also mention it in the sheet.
If the Created By name is missing, you should mention it as an issue in the sheet.
If a call type name is not available in Caryfy, you do not need to update the data. Simply mention it in the sheet.
If you find any extra Call Types in Carebravo compared to Caryfy, do not update the data. Simply mention it in the sheet.`,

    "Skills": `In the Skill section, the data selected in Carebravo should exactly match the data available in Caryfy.
If you find any missing data, you need to update it in Carebravo according to the data in Caryfy.
In the Skill section, any documents uploaded in Caryfy will be visible in the Employee Document section. You need to verify them there. If any documents are missing, you must download them from Caryfy and upload them to Carebravo.
In the Skill section, you will see an Internal Document section in Caryfy, but this section is not available in Carebravo. Therefore, if there are any internal documents in Caryfy, you need to go to the Employee Document section in Carebravo and upload them there.`,

    "Documents": `In the Client Documents section, you need to match the latest (top) documents.
You must verify the Created Date of the document. It should be the same in Caryfy and Carebravo.
In Caryfy, there is no option for an Expiry Date, so this is not an issue. You can ignore it.
In Carebravo, documents uploaded in the Skill Section will be shown there.
You need to verify these documents as per Caryfy.
In the Skill Section, documents will have an Expiry Date, so you must verify the expiry date.
If you find any random documents missing, download them from Caryfy and upload them to Carebravo.
If multiple documents are missing for all clients, mention this in the sheet.
`
  },
};

/**
 * Helper to get a field description by entity type and field name.
 */
export const getFieldDescription = (entityType: string, fieldName: string): string | undefined =>
  FIELD_DESCRIPTIONS[entityType]?.[fieldName];
